'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@planner/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { Badge } from '@planner/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@planner/ui'
import { usePackageConfigurator } from '@/features/menu/hooks/usePackageConfigurator'
import { useMenuPackages } from '@/features/menu/hooks/useMenu'
import { useCompanySettings } from '@/features/contracts/hooks/useCompanySettings'
import { EventServicesConfigurator } from '@/features/services/components/EventServicesConfigurator'
import type { MenuPackageCategory, MenuPackageItem, Event, MenuPackage } from '@planner/types'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { pluralizeDish } from '@/lib/utils'

interface MenuPackageConfiguratorProps {
  eventId: string
  packageId: string
  event: Pick<Event, 'id' | 'number_of_guests'>
  onClose: () => void
  wizardMode?: boolean
  onConfigured?: (selections: Record<string, {
    categoryId: string
    selectedItems: Array<{
      menu_item_id: string
      quantity_percentage: number
    }>
  }>) => void
  onConvertToCustom?: (customItems: Array<{
    menu_item_id: string
    quantity_percentage: number
  }>) => void
}

export function MenuPackageConfigurator({
  eventId,
  packageId,
  event,
  onClose,
  wizardMode = false,
  onConfigured,
  onConvertToCustom,
}: MenuPackageConfiguratorProps) {
  const router = useRouter()
  const { packages: menuPackages } = useMenuPackages()
  const { settings: companySettings } = useCompanySettings()
  const {
    packageCategories,
    packageItems,
    allMenuItems,
    selections,
    loading,
    error,
    fetchPackageData,
    toggleItemSelection,
    updateItemPercentage,
    validateCategoryLimits,
    validateAllCategories,
    calculateTotalPrice,
    saveSelections,
    getAllItemsForCategory,
  } = usePackageConfigurator(packageId, eventId, event.number_of_guests)
  const [saving, setSaving] = useState(false)
  const [showAddItemForCategory, setShowAddItemForCategory] = useState<Record<string, boolean>>({})
  const [selectedCustomItemForCategory, setSelectedCustomItemForCategory] = useState<Record<string, string>>({})
  const hasAutoConvertedRef = useRef(false)
  const [showToast, setShowToast] = useState(false)
  const [showConvertDialog, setShowConvertDialog] = useState(false)
  const [pendingConvertItems, setPendingConvertItems] = useState<Array<{
    menu_item_id: string
    quantity_percentage: number
  }> | null>(null)
  const [exceededCategoryId, setExceededCategoryId] = useState<string | null>(null)
  const [lastAddedItemId, setLastAddedItemId] = useState<string | null>(null)

  useEffect(() => {
    fetchPackageData()
    // Reset conversion flag when package changes
    hasAutoConvertedRef.current = false
  }, [fetchPackageData])

  // Real-time validation and show dialog for conversion to custom menu
  useEffect(() => {
    // Skip if already converted or missing data
    if (hasAutoConvertedRef.current || !packageCategories.length || !selections || Object.keys(selections).length === 0) {
      return
    }

    // Dialog will show in both wizard mode (with onConvertToCustom) and normal mode (redirects to custom menu page)

    // Skip if dialog is already open
    if (showConvertDialog) {
      return
    }

    const validation = validateAllCategories()
    if (!validation.valid && validation.invalidCategories.length > 0) {
      // Check if any category exceeds max_selections (not just min)
      const exceededCategory = validation.invalidCategories.find((invalid) => {
        const category = packageCategories.find((cat) => cat.id === invalid.categoryId)
        if (!category) return false
        const selection = selections[invalid.categoryId]
        const count = selection?.selectedItems.length || 0
        return count > category.max_selections
      })

      if (exceededCategory && !hasAutoConvertedRef.current && !showConvertDialog) {
        // Mark as converted immediately to prevent loops
        hasAutoConvertedRef.current = true

        // Find the category that exceeded the limit
        const category = packageCategories.find((cat) => cat.id === exceededCategory.categoryId)
        if (!category) return

        const selection = selections[exceededCategory.categoryId]
        // Find the last added item (the one that caused the exceed)
        // We'll use the last item in the selection as the one that was just added
        const lastItem = selection?.selectedItems[selection.selectedItems.length - 1]

        setExceededCategoryId(exceededCategory.categoryId)
        setLastAddedItemId(lastItem?.menu_item_id || null)

        // Convert package selections to custom menu items
        const customItems: Array<{
          menu_item_id: string
          quantity_percentage: number
        }> = []

        Object.values(selections).forEach((selection) => {
          selection.selectedItems.forEach((item) => {
            // Avoid duplicates
            if (!customItems.some((ci) => ci.menu_item_id === item.menu_item_id)) {
              customItems.push({
                menu_item_id: item.menu_item_id,
                quantity_percentage: item.quantity_percentage,
              })
            } else {
              // If duplicate, merge percentages (take max)
              const existing = customItems.find((ci) => ci.menu_item_id === item.menu_item_id)
              if (existing) {
                existing.quantity_percentage = Math.max(
                  existing.quantity_percentage,
                  item.quantity_percentage
                )
              }
            }
          })
        })

        // Only convert if we have items
        if (customItems.length > 0) {
          // Show dialog for conversion
          setPendingConvertItems(customItems)
          setShowConvertDialog(true)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, packageCategories, wizardMode, showConvertDialog])

  const handleSave = async () => {
    const validation = validateAllCategories()
    if (!validation.valid) {
      // If validation fails, show dialog to convert to custom menu
      const customItems: Array<{
        menu_item_id: string
        quantity_percentage: number
      }> = []

      Object.values(selections).forEach((selection) => {
        selection.selectedItems.forEach((item) => {
          if (!customItems.some((ci) => ci.menu_item_id === item.menu_item_id)) {
            customItems.push({
              menu_item_id: item.menu_item_id,
              quantity_percentage: item.quantity_percentage,
            })
          }
        })
      })

      if (customItems.length > 0) {
        setPendingConvertItems(customItems)
        setShowConvertDialog(true)
      }
      return
    }

    if (wizardMode && onConfigured) {
      // In wizard mode, just call the callback
      onConfigured(selections)
      return
    }

    setSaving(true)
    const result = await saveSelections()
    setSaving(false)

    if (result.error) {
      alert(`Błąd podczas zapisywania: ${result.error}`)
    } else {
      onClose()
      router.refresh()
    }
  }

  // In wizard mode, call onConfigured whenever selections change
  useEffect(() => {
    if (wizardMode && onConfigured) {
      onConfigured(selections)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, wizardMode])

  const convertPackageToCustom = () => {
    // Convert package selections to custom menu items
    const customItems: Array<{
      menu_item_id: string
      quantity_percentage: number
    }> = []

    Object.values(selections).forEach((selection) => {
      selection.selectedItems.forEach((item) => {
        // Avoid duplicates
        if (!customItems.some((ci) => ci.menu_item_id === item.menu_item_id)) {
          customItems.push({
            menu_item_id: item.menu_item_id,
            quantity_percentage: item.quantity_percentage,
          })
        } else {
          // If duplicate, merge percentages (take max)
          const existing = customItems.find((ci) => ci.menu_item_id === item.menu_item_id)
          if (existing) {
            existing.quantity_percentage = Math.max(
              existing.quantity_percentage,
              item.quantity_percentage
            )
          }
        }
      })
    })

    if (wizardMode && onConvertToCustom) {
      // In wizard mode, call the conversion callback
      onConvertToCustom(customItems)
    } else {
      // In normal mode, redirect to custom menu page
      // Store items in sessionStorage to restore them
      sessionStorage.setItem(`customMenuItems_${eventId}`, JSON.stringify(customItems))
      router.push(`/events/${eventId}/menu/custom`)
    }
  }

  const handleConfirmConvert = () => {
    if (pendingConvertItems) {
      if (wizardMode && onConvertToCustom) {
        // In wizard mode, call the conversion callback
        onConvertToCustom(pendingConvertItems)
      } else {
        // In normal mode, redirect to custom menu page
        // Store items in sessionStorage to restore them
        sessionStorage.setItem(`customMenuItems_${eventId}`, JSON.stringify(pendingConvertItems))
        router.push(`/events/${eventId}/menu/custom`)
      }
      setShowConvertDialog(false)
      setPendingConvertItems(null)
      setExceededCategoryId(null)
      setLastAddedItemId(null)
      hasAutoConvertedRef.current = true
    }
  }

  const handleCancelConvert = () => {
    // Reset the conversion flag
    hasAutoConvertedRef.current = false
    
    // Unselect the last added item that caused the exceed
    if (exceededCategoryId && lastAddedItemId) {
      toggleItemSelection(exceededCategoryId, lastAddedItemId, true)
    }
    
    setShowConvertDialog(false)
    setPendingConvertItems(null)
    setExceededCategoryId(null)
    setLastAddedItemId(null)
  }

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      // When dialog is closed (by X, clicking outside, or ESC), cancel the conversion
      handleCancelConvert()
    } else {
      setShowConvertDialog(true)
    }
  }

  const calculateCustomMenuFee = () => {
    // First check if package has custom menu fee
    const pkg = menuPackages.find((p) => p.id === packageId)
    if (pkg?.custom_menu_fee_enabled && pkg.custom_menu_fee_amount) {
      if (pkg.custom_menu_fee_type === 'per_person') {
        return pkg.custom_menu_fee_amount * event.number_of_guests
      }
      return pkg.custom_menu_fee_amount
    }
    
    // If package doesn't have fee, check global company settings
    if (companySettings?.custom_menu_fee_enabled && companySettings.custom_menu_fee_amount) {
      if (companySettings.custom_menu_fee_type === 'per_person') {
        return companySettings.custom_menu_fee_amount * event.number_of_guests
      }
      return companySettings.custom_menu_fee_amount
    }
    
    return 0
  }

  const handleChangePackage = () => {
    if (confirm('Czy na pewno chcesz zmienić pakiet? Utracisz wszystkie wprowadzone zmiany.')) {
      router.push(`/events/${eventId}/menu`)
    }
  }

  if (loading) {
    return <div className="p-8">Ładowanie...</div>
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="text-destructive">Błąd: {error}</div>
      </div>
    )
  }

  const getCategorySelection = (categoryId: string) => {
    return selections[categoryId] || { categoryId, selectedItems: [] }
  }

  const getItemPrice = (itemId: string, categoryId: string): number => {
    // First check if it's in package items
    const packageItem = packageItems.find(
      (pi) => pi.menu_item_id === itemId && pi.menu_package_category_id === categoryId
    )
    if (packageItem) {
      // Return 0 for unlimited items
      if (packageItem.is_unlimited) {
        return 0
      }
      return packageItem.price_per_person || packageItem.menu_item?.price || 0
    }
    // Otherwise get from allMenuItems
    const menuItem = allMenuItems.find((mi) => mi.id === itemId)
    return menuItem?.price || 0
  }

  const isItemInPackage = (itemId: string, categoryId: string): boolean => {
    return packageItems.some(
      (pi) => pi.menu_item_id === itemId && pi.menu_package_category_id === categoryId
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast notification */}
      {showToast && (
        <Card className="border-primary bg-primary/10 animate-in slide-in-from-top-5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-primary mb-1">Przełączono na menu niestandardowe</h3>
                <p className="text-sm text-muted-foreground">
                  Przekroczono limity pakietu. Wszystkie wybrane dania zostały zachowane w menu niestandardowym.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowToast(false)}
                className="h-6 w-6"
              >
                ×
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Konfiguracja pakietu menu</CardTitle>
              {packageId && menuPackages.length > 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  Wybrany pakiet: <span className="font-medium">{menuPackages.find((pkg) => pkg.id === packageId)?.name || 'N/A'}</span>
                </p>
              )}
            </div>
            <Button variant="outline" onClick={handleChangePackage}>
              Zmień pakiet
            </Button>
          </div>
        </CardHeader>
      </Card>


      {packageCategories.map((category: MenuPackageCategory) => {
        const { packageItems: itemsFromPackage, otherItems } = getAllItemsForCategory(category.id)
        const selection = getCategorySelection(category.id)
        const validation = validateCategoryLimits(category.id)
        const categoryName = (category as MenuPackageCategory & { menu_category?: { name: string } }).menu_category?.name || 'Kategoria'
        const showAddItem = showAddItemForCategory[category.id] || false
        const selectedCustomItem = selectedCustomItemForCategory[category.id] || ''
        const totalItemsCount = itemsFromPackage.length + otherItems.length
        
        // Show ALL available items from category, not just up to max_selections
        // First add all items from package, then add all other items from category
        const allCategoryItemsForDisplay = [
          ...itemsFromPackage,
          ...otherItems.map(item => {
            // Create a temporary MenuPackageItem-like object for display
            const packageItem = packageItems.find(pi => pi.menu_item_id === item.id && pi.menu_package_category_id === category.id)
            if (packageItem) return packageItem
            
            // If not in package, create a display item (will be charged extra)
            return {
              id: `temp-${item.id}`,
              menu_package_category_id: category.id,
              menu_item_id: item.id,
              menu_item: item,
              price_per_person: item.price,
              is_unlimited: false,
              allow_percentage_selection: false,
              display_order: 999,
            } as MenuPackageItem & { menu_item?: MenuItem }
          })
        ]
        
        // Get remaining other items that are not displayed in the main section
        const displayedItemIds = new Set(allCategoryItemsForDisplay.map(item => item.menu_item_id))
        const remainingOtherItems = otherItems.filter(item => !displayedItemIds.has(item.id))

        return (
          <Card key={category.id}>
            <CardHeader>
              <CardTitle>
                {categoryName}
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  Wybierz {category.max_selections} z {totalItemsCount} {pluralizeDish(totalItemsCount)}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!validation.valid && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  {validation.message}
                </div>
              )}

              {/* Package items */}
              {allCategoryItemsForDisplay.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-muted-foreground">Dania z pakietu</h4>
                  <div className="space-y-3">
                    {allCategoryItemsForDisplay.map((item: MenuPackageItem) => {
                      const menuItem = (item as MenuPackageItem & { menu_item?: { id: string; name: string; description?: string; price: number } }).menu_item
                      if (!menuItem) return null

                      const isSelected = selection.selectedItems.some(
                        (si) => si.menu_item_id === item.menu_item_id
                      )
                      const selectedItem = selection.selectedItems.find(
                        (si) => si.menu_item_id === item.menu_item_id
                      )
                      const isUnlimited = item.is_unlimited || false
                      const price = isUnlimited ? 0 : (item.price_per_person || menuItem.price)
                      const quantity = selectedItem
                        ? Math.ceil((selectedItem.quantity_percentage / 100) * event.number_of_guests)
                        : 0
                      const itemTotal = price * quantity

                      return (
                        <div
                          key={item.id}
                          className={`p-4 border rounded-lg ${
                            isSelected ? 'bg-primary/5 border-primary' : ''
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex items-center pt-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() =>
                                  toggleItemSelection(category.id, item.menu_item_id, item.allow_percentage_selection)
                                }
                                className="w-4 h-4"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <div className="font-medium">{menuItem.name}</div>
                                    {isUnlimited && (
                                      <Badge variant="secondary">Bez limitu</Badge>
                                    )}
                                    {item.id.startsWith('temp-') && (
                                      <Badge variant="outline">Za dopłatą</Badge>
                                    )}
                                  </div>
                                  {menuItem.description && (
                                    <div className="text-sm text-muted-foreground mt-1">
                                      {menuItem.description}
                                    </div>
                                  )}
                                  <div className="text-sm font-medium mt-2">
                                    {isUnlimited ? '0.00 zł / osoba' : `${price.toFixed(2)} zł / osoba`}
                                  </div>
                                </div>
                              </div>

                              {isSelected && item.allow_percentage_selection && (
                                <div className="mt-4 space-y-2">
                                  <Label htmlFor={`percentage-${item.id}`}>
                                    Dla ilu % gości? ({quantity} porcji)
                                  </Label>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      id={`percentage-${item.id}`}
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={selectedItem?.quantity_percentage || 100}
                                      onChange={(e) =>
                                        updateItemPercentage(
                                          category.id,
                                          item.menu_item_id,
                                          parseInt(e.target.value) || 0
                                        )
                                      }
                                      className="w-24"
                                    />
                                    <span className="text-sm text-muted-foreground">%</span>
                                    <div className="flex-1 text-right font-medium">
                                      Razem: {itemTotal.toFixed(2)} zł
                                    </div>
                                  </div>
                                </div>
                              )}

                              {isSelected && !item.allow_percentage_selection && (
                                <div className="mt-2 text-sm text-muted-foreground">
                                  Dla wszystkich gości ({event.number_of_guests} porcji) -{' '}
                                  <span className="font-medium">{itemTotal.toFixed(2)} zł</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Other items from category - only show remaining items not displayed above */}
              {remainingOtherItems.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Inne dania z kategorii</h4>
                    {!showAddItem && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAddItemForCategory({ ...showAddItemForCategory, [category.id]: true })}
                      >
                        Dodaj danie za dopłatą
                      </Button>
                    )}
                  </div>
                  {showAddItem && (
                    <div className="p-3 border rounded-lg bg-muted/50 space-y-2">
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={selectedCustomItem}
                        onChange={(e) => {
                          const itemId = e.target.value
                          if (itemId) {
                            toggleItemSelection(category.id, itemId, true)
                            setSelectedCustomItemForCategory({ ...selectedCustomItemForCategory, [category.id]: '' })
                            setShowAddItemForCategory({ ...showAddItemForCategory, [category.id]: false })
                          } else {
                            setSelectedCustomItemForCategory({ ...selectedCustomItemForCategory, [category.id]: itemId })
                          }
                        }}
                      >
                        <option value="">-- Wybierz danie --</option>
                        {remainingOtherItems
                          .filter((item) => !selection.selectedItems.some((si) => si.menu_item_id === item.id))
                          .map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name} - {item.price.toFixed(2)} zł/os
                            </option>
                          ))}
                      </select>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowAddItemForCategory({ ...showAddItemForCategory, [category.id]: false })
                            setSelectedCustomItemForCategory({ ...selectedCustomItemForCategory, [category.id]: '' })
                          }}
                        >
                          Anuluj
                        </Button>
                      </div>
                    </div>
                  )}
                  {selection.selectedItems
                    .filter((si) => !isItemInPackage(si.menu_item_id, category.id))
                    .map((selectedItem) => {
                      const menuItem = allMenuItems.find((mi) => mi.id === selectedItem.menu_item_id)
                      if (!menuItem) return null

                      const price = getItemPrice(selectedItem.menu_item_id, category.id)
                      const quantity = Math.ceil((selectedItem.quantity_percentage / 100) * event.number_of_guests)
                      const itemTotal = price * quantity

                      return (
                        <div
                          key={selectedItem.menu_item_id}
                          className="p-4 border rounded-lg bg-primary/5 border-primary"
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex items-center pt-1">
                              <input
                                type="checkbox"
                                checked={true}
                                onChange={() =>
                                  toggleItemSelection(category.id, selectedItem.menu_item_id, true)
                                }
                                className="w-4 h-4"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-medium">{menuItem.name} (dodatkowe)</div>
                                  {menuItem.description && (
                                    <div className="text-sm text-muted-foreground mt-1">
                                      {menuItem.description}
                                    </div>
                                  )}
                                  <div className="text-sm font-medium mt-2">
                                    {price.toFixed(2)} zł / osoba
                                  </div>
                                </div>
                              </div>

                              <div className="mt-4 space-y-2">
                                <Label htmlFor={`percentage-custom-${selectedItem.menu_item_id}`}>
                                  Dla ilu % gości? ({quantity} porcji)
                                </Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    id={`percentage-custom-${selectedItem.menu_item_id}`}
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={selectedItem.quantity_percentage}
                                    onChange={(e) =>
                                      updateItemPercentage(
                                        category.id,
                                        selectedItem.menu_item_id,
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                    className="w-24"
                                  />
                                  <span className="text-sm text-muted-foreground">%</span>
                                  <div className="flex-1 text-right font-medium">
                                    Razem: {itemTotal.toFixed(2)} zł
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </div>
              )}

              {allCategoryItemsForDisplay.length === 0 && remainingOtherItems.length === 0 && (
                <p className="text-muted-foreground text-center py-4">
                  Brak dostępnych dań w tej kategorii
                </p>
              )}
            </CardContent>
          </Card>
        )
      })}

      {!wizardMode && (
        <EventServicesConfigurator
          eventId={eventId}
          numberOfGuests={event.number_of_guests}
        />
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-medium">Cena całkowita:</span>
            <span className="text-3xl font-bold">{calculateTotalPrice().toFixed(2)} zł</span>
          </div>
          {!wizardMode && (
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Anuluj
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Zapisywanie...' : 'Zapisz'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog for converting to custom menu */}
      <Dialog open={showConvertDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Przekroczono limit pakietu</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>
                Wybrałeś więcej dań niż pozwala na to pakiet. Przełącz się na menu niestandardowe, aby zachować wszystkie wybrane dania.
              </p>
              {(() => {
                const fee = calculateCustomMenuFee()
                if (fee > 0) {
                  return (
                    <p className="font-medium text-foreground">
                      Dodatkowa opłata: {fee.toFixed(2)} zł
                    </p>
                  )
                }
                return null
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 sm:mt-6">
            <Button variant="outline" onClick={handleCancelConvert}>
              Anuluj
            </Button>
            <Button onClick={handleConfirmConvert}>
              Przełącz na menu niestandardowe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

